import { useState, useEffect, useRef } from "react";
import api from "../../api/client";

/**
 * NapMergeQrModal handles the merge QR flow:
 * - Shows QR preview for combined records
 * - Allows entering Used Port/Vacant/ONU Connect
 * - Confirms merge (deletes originals, creates merged record)
 */
export default function NapMergeQrModal({ previewData, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [usedPort, setUsedPort] = useState("");
  const [vacant, setVacant] = useState("");
  const [onuConnect, setOnuConnect] = useState("");
  const onuManuallyEdited = useRef(false);

  // Auto-compute ONU Connect
  useEffect(() => {
    if (onuManuallyEdited.current) return;
    const ports = previewData?.no_of_ports;
    const u = usedPort === "" ? null : parseInt(usedPort, 10);
    const v = vacant === "" ? null : parseInt(vacant, 10);
    if (ports != null && u != null && v != null) {
      const computed = Math.max(0, ports - u - v);
      setOnuConnect(String(computed));
    }
  }, [usedPort, vacant, previewData?.no_of_ports]);

  const handleNumericInput = (setter) => (e) => {
    const val = e.target.value;
    if (val === "" || (/^\d{1,2}$/.test(val) && parseInt(val, 10) >= 0)) {
      setter(val);
    }
  };

  const handleSaveClick = () => {
    if (usedPort === "" || usedPort === null) {
      setError("Used Port is required.");
      return;
    }
    if (vacant === "" || vacant === null) {
      setError("Unused/Vacant Port is required.");
      return;
    }
    if (onuConnect === "" || onuConnect === null) {
      setError("ONU Connect is required.");
      return;
    }
    setError("");
    setShowConfirm(true);
  };

  const handleConfirmMerge = async () => {
    setSaving(true);
    setError("");
    try {
      const body = {
        record_ids: previewData.record_ids,
        used_port: parseInt(usedPort, 10),
        vacant: parseInt(vacant, 10),
        onu_connect: parseInt(onuConnect, 10),
      };
      await api.post("/nap-locator/merge-qr/save", body);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to merge and save QR.");
      setShowConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  // Confirmation popup
  if (showConfirm) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
          <h2 className="text-lg font-semibold mb-3">Confirm Merge</h2>
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to merge <strong>{previewData.record_ids.length} records</strong> into one?
          </p>
          <p className="text-sm text-gray-600 mb-4">
            The original records will be deleted and replaced with:<br/>
            <strong>{previewData.merged_nap_code}</strong>
          </p>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowConfirm(false)}
              className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmMerge}
              disabled={saving}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Merging…" : "Yes, Merge & Save"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-lg font-semibold">Merge QR Preview</h2>
          <span className="text-sm text-gray-600 font-medium">{previewData.merged_nap_code}</span>
        </div>

        {/* QR Image */}
        {previewData.qr_code_data && (
          <div className="flex justify-center mb-4">
            <img
              src={`data:image/png;base64,${previewData.qr_code_data}`}
              alt="QR Code"
              className="w-64 h-64 border rounded"
            />
          </div>
        )}

        {/* Merged record summary */}
        <div className="bg-gray-50 rounded p-3 mb-4 text-sm space-y-1">
          <p><span className="text-gray-500">NAP Code:</span> <strong>{previewData.merged_nap_code}</strong></p>
          <p><span className="text-gray-500">No of Ports:</span> {previewData.no_of_ports}</p>
          <p><span className="text-gray-500">Latitude:</span> {previewData.latitude ?? "—"}</p>
          <p><span className="text-gray-500">Longitude:</span> {previewData.longitude ?? "—"}</p>
          <p><span className="text-gray-500">Area:</span> {previewData.area ?? "—"}</p>
          <p><span className="text-gray-500">Records to merge:</span> {previewData.record_ids.length}</p>
        </div>

        {/* Used Port, Vacant & ONU Connect */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Used Port <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={usedPort}
              onChange={handleNumericInput(setUsedPort)}
              placeholder="0-99"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Unused/Vacant <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={vacant}
              onChange={handleNumericInput(setVacant)}
              placeholder="0-99"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">ONU Connect <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={onuConnect}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || (/^\d{1,2}$/.test(val) && parseInt(val, 10) >= 0)) {
                  onuManuallyEdited.current = true;
                  setOnuConnect(val);
                }
              }}
              placeholder="0-99"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveClick}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Save QR Code
          </button>
        </div>
      </div>
    </div>
  );
}
